import type { SeriesOption, XAXisComponentOption, YAXisComponentOption } from 'echarts';
import { chartDefaults, mergeTooltipConfig, mergeSeriesConfig } from './use-default-options';
import useBoundaryCategoryChart from './use-boundary-category-chart';
import { roundMaxToUpperZero, roundMinToLowerZero } from './common-functions';

export const makeYAxis = (type: string, formatter: (value: any) => any, name?: string) => ({
  type: type,
  position: 'right',
  name,
  nameLocation: 'center',
  nameGap: 50,
  nameTextStyle: {
    color: '#FFFF00',
    fontSize: 12,
    fontFamily: 'Inter',
    fontWeight: 400,
    lineHeight: 16,
  },
  axisPointer: { label: { margin: 10 } },
  axisLabel: {
    formatter: formatter || ((value: any) => value),
  },
});

interface UseOptionsProps {
  series: Array<Pick<SeriesOption, 'type' | 'data' | 'color' | 'labelLine'> & { key?: string }>;
  xAxis: XAXisComponentOption & {
    formatter?: (value: any) => string;
    data?: string[] | any[];
    name?: string;
  };
  yAxis: YAXisComponentOption & {
    axisPointer?: { label: { margin: number } };
    axisLabel?: { formatter: (value: any) => string };
    name?: string;
  };
  legend?: any;
  grid?: any;
  tooltip?: any & {
    xAxisTitle?: string;
    yAxisTitle?: string;
    valueFormatter?: (value: any) => string;
    xAxisFormatter?: (value: any) => string;
    yAxisFormatter?: (value: any) => string;
  };
  visualMap?: any;
  boundaryGap?: boolean;
}

export function useOptions({
  series,
  xAxis,
  yAxis,
  legend,
  grid,
  tooltip,
  visualMap,
  boundaryGap,
}: UseOptionsProps) {
  const processedSeries = mergeSeriesConfig(series, chartDefaults.defaultSeriesConfig);

  const xAxisConfig = {
    ...chartDefaults.xAxis,
    ...xAxis,
    data: xAxis.data || [],
    axisLabel: {
      ...chartDefaults.xAxis.axisLabel,
      ...xAxis.axisLabel,
    },
  };

  const yAxisConfig = {
    ...chartDefaults.yAxis,
    min: (v: any) => roundMinToLowerZero(v.min as number),
    max: (v: any) => roundMaxToUpperZero(v.max as number),
    ...yAxis,
    axisLabel: {
      ...chartDefaults.yAxis.axisLabel,
      ...yAxis.axisLabel,
    },
  };

  const options = {
    grid: { ...chartDefaults.grid, ...grid },
    xAxis: xAxisConfig,
    yAxis: yAxisConfig,
    legend: { ...chartDefaults.legend, ...legend },
    series: processedSeries.map((serie) => ({
      ...serie,
      data: serie.data as any,
    })),
    tooltip: mergeTooltipConfig(tooltip),
    ...(visualMap ? { visualMap } : {}),
  };

  const boundaryGapOption = useBoundaryCategoryChart({ options });

  if (boundaryGap) {
    return boundaryGapOption;
  }

  return options;
}
